// Ai_CSPM - Cloud Security Posture Management dashboard
// Uses existing Tanikkai UI structure and API service style

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from '../utils/notify';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import Pagination from './ui/pagination';
import { DataTable } from './common/DataTable';
import {
  CloudIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { Eye, Pencil, Trash2 } from 'lucide-react';

import DashboardOverview from "./DashboardOverview";
import ApiLoader from "./ui/ApiLoader";
import { TabNavigation } from "./ui";
import CRMModuleHeader from "./ui/CRMModuleHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const DEFAULT_PAGE_SIZE = 10;
const CSPM_BLUE = "#2F2A7E";

const tabs = [
  { id: 'dashboard', label: 'Dashboard', icon: ShieldCheckIcon },
  { id: 'assets', label: 'Asset Inventory', icon: CloudIcon },
  { id: 'rules', label: 'Rules', icon: CheckCircleIcon },
  { id: 'findings', label: 'Findings', icon: ExclamationTriangleIcon },
  { id: 'remediation', label: 'Remediation', icon: ArrowPathIcon }
];

const statusStyles = {
  Connected: 'bg-green-100 text-green-800 border-green-200',
  Disconnected: 'bg-red-100 text-red-800 border-red-200',
  // CSPMFinding.status choices (ai_cspm/models.py FINDING_STATUS_CHOICES)
  // - lowercase, matched exactly by the backend filter.
  open: 'bg-red-100 text-red-800 border-red-200',
  in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  resolved: 'bg-green-100 text-green-800 border-green-200',
  closed: 'bg-slate-100 text-slate-800 border-slate-200',
  suppressed: 'bg-blue-100 text-blue-800 border-blue-200'
};

// CSPMRemediationAction has no real `status` field (see
// ai_cspm/models.py) - its only real classification field is
// `action_type` (choices: manual_fix, auto_remediated, suppressed,
// assigned, ai_generated). Badge styling for the Remediation tab is
// keyed off that instead of a fabricated status.
const actionTypeStyles = {
  manual_fix: 'bg-blue-100 text-blue-800 border-blue-200',
  auto_remediated: 'bg-green-100 text-green-800 border-green-200',
  suppressed: 'bg-slate-100 text-slate-800 border-slate-200',
  assigned: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  ai_generated: 'bg-purple-100 text-purple-800 border-purple-200'
};

const severityStyles = {
  High: 'bg-red-100 text-red-800 border-red-200',
  Medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  Low: 'bg-blue-100 text-blue-800 border-blue-200'
};

const priorityStyles = {
  High: 'bg-red-100 text-red-800 border-red-200',
  Medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  Low: 'bg-blue-100 text-blue-800 border-blue-200'
};

// ===========================================================
// Shared modal header - matches the Trust Center "Add Control"
// popup styling: dark blue header, white title/subtitle, close
// icon top-right. Reused by Create Rule / Edit Rule / View Rule.
// ===========================================================
const ModalHeader = ({ title, subtitle, onClose }) => (
  <div
    className="flex items-start justify-between rounded-t-2xl px-6 py-5"
    style={{ backgroundColor: CSPM_BLUE }}
  >
    <div>
      <h2 className="text-lg font-semibold text-white">{title}</h2>
      {subtitle ? (
        <p className="mt-1 text-sm text-white/70">{subtitle}</p>
      ) : null}
    </div>
    <button
      type="button"
      onClick={onClose}
      className="ml-4 rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
      aria-label="Close"
    >
      <XMarkIcon className="h-5 w-5" />
    </button>
  </div>
);

// Normalize a risk record so the UI can safely read id / name /
// framework / description regardless of the exact backend field names.
const getRiskId = (risk) => risk?.risk_id ?? risk?.id ?? '';
const getRiskName = (risk) =>
  risk?.risk_name || risk?.name || risk?.risk_title || risk?.title || risk?.risk_description || getRiskId(risk);
const getRiskFramework = (risk) =>
  risk?.regulatory_framework || risk?.regulatory_mapping || risk?.framework_mapping || risk?.framework || risk?.mapping || '';
const getRiskDescription = (risk) => risk?.risk_description || risk?.description || '';

// Normalize a CIS Control record (either the /checks/cis-controls/
// options shape {id, name} or the stored mapping shape
// {control_id, control_title}) so the UI can read either the same
// way. `id`/control_id here is the real CIS v8 safeguard id as
// seeded in the Trust Center (e.g. "4.1", "6.7") - displayed as-is,
// never replaced with a synthetic "Control N" label.
const getCisControlId = (control) => control?.control_id ?? control?.id ?? '';
const getCisControlName = (control) => control?.control_title ?? control?.name ?? getCisControlId(control);

// ===========================================================
// Searchable multi-select "Risk Mapping" dropdown with checkboxes,
// select all / deselect all, and a scrollable options list.
// ===========================================================
const RiskMultiSelectDropdown = ({ riskList = [], selectedIds, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = React.useRef(null);
  const safeSelectedIds = Array.isArray(selectedIds) ? selectedIds : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    // IMPORTANT: listen on 'click', not 'mousedown'. React's onClick handlers
    // fire during the bubble phase at the app root, which happens BEFORE a
    // document-level 'click' listener added here. If we used 'mousedown',
    // this handler would remove the dropdown from the DOM before 'mouseup'
    // fires, which can suppress the click event on whatever button the user
    // was actually trying to press underneath/near the dropdown (e.g. Save
    // Changes) — the classic "backdrop swallows the click" bug.
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const filteredRisks = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return riskList;
    return riskList.filter((risk) => {
      const id = String(getRiskId(risk)).toLowerCase();
      const name = String(getRiskName(risk)).toLowerCase();
      return id.includes(term) || name.includes(term);
    });
  }, [riskList, search]);

  const toggleRisk = (riskId) => {
    if (disabled) return;
    const exists = safeSelectedIds.includes(riskId);
    const next = exists
      ? safeSelectedIds.filter((id) => id !== riskId)
      : [...safeSelectedIds, riskId];
    onChange(next);
  };

  const selectAll = () => {
    if (disabled) return;
    const allIds = filteredRisks.map((risk) => getRiskId(risk));
    const merged = Array.from(new Set([...safeSelectedIds, ...allIds]));
    onChange(merged);
  };

  const deselectAll = () => {
    if (disabled) return;
    if (search.trim()) {
      const visibleIds = new Set(filteredRisks.map((risk) => getRiskId(risk)));
      onChange(safeSelectedIds.filter((id) => !visibleIds.has(id)));
    } else {
      onChange([]);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-slate-50 h-10 px-4 text-sm text-slate-900 focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className={safeSelectedIds.length ? 'text-slate-900' : 'text-slate-400'}>
          {safeSelectedIds.length > 0
            ? `Risk Mapping (${safeSelectedIds.length} Selected)`
            : 'Select Risks'}
        </span>
        <ChevronDownIcon className={`h-4 w-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && !disabled && (
        <div className="absolute z-20 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 p-3">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by Risk ID or Risk Name"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 h-9 pl-9 pr-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none"
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <button type="button" onClick={selectAll} className="font-medium text-[#2F2A7E] hover:underline">
                Select All
              </button>
              <button type="button" onClick={deselectAll} className="font-medium text-slate-500 hover:underline">
                Deselect All
              </button>
            </div>
          </div>

          <div className="max-h-56 overflow-y-auto py-1">
            {filteredRisks.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-slate-400">No risks found.</p>
            ) : (
              filteredRisks.map((risk) => {
                const riskId = getRiskId(risk);
                const checked = safeSelectedIds.includes(riskId);
                return (
                  <div
                    key={riskId}
                    role="checkbox"
                    aria-checked={checked}
                    tabIndex={0}
                    onClick={() => toggleRisk(riskId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleRisk(riskId);
                      }
                    }}
                    className="flex cursor-pointer items-start gap-3 px-4 py-2 text-sm hover:bg-slate-50"
                  >
                    <span
                      className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                        checked ? 'border-[#2F2A7E] bg-[#2F2A7E]' : 'border-slate-300 bg-white'
                      }`}
                    >
                      {checked && <CheckIcon className="h-3 w-3 text-white" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-medium text-slate-900">{riskId} - {getRiskName(risk)}</span>
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ===========================================================
// Searchable multi-select "CIS Controls" dropdown with checkboxes.
// Same interaction pattern as RiskMultiSelectDropdown, but the
// options list is fetched from the backend's
// /checks/cis-controls/ endpoint (CSPM-relevant CIS Controls v8
// only, from the Trust Center Framework) - never hardcoded here.
// Selected values are stored as [{control_id, control_title}].
// ===========================================================
const CisControlsMultiSelectDropdown = ({ controlList = [], selected, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = React.useRef(null);
  const safeSelected = Array.isArray(selected) ? selected : [];
  const selectedIds = safeSelected.map(getCisControlId);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const filteredControls = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return controlList;
    return controlList.filter((control) => {
      const id = String(getCisControlId(control)).toLowerCase();
      const name = String(getCisControlName(control)).toLowerCase();
      return id.includes(term) || name.includes(term);
    });
  }, [controlList, search]);

  const toggleControl = (control) => {
    if (disabled) return;
    const id = getCisControlId(control);
    const exists = selectedIds.includes(id);
    const next = exists
      ? safeSelected.filter((c) => getCisControlId(c) !== id)
      : [...safeSelected, { control_id: id, control_title: getCisControlName(control) }];
    onChange(next);
  };

  const selectAll = () => {
    if (disabled) return;
    const existingIds = new Set(selectedIds);
    const additions = filteredControls
      .filter((c) => !existingIds.has(getCisControlId(c)))
      .map((c) => ({ control_id: getCisControlId(c), control_title: getCisControlName(c) }));
    onChange([...safeSelected, ...additions]);
  };

  const deselectAll = () => {
    if (disabled) return;
    if (search.trim()) {
      const visibleIds = new Set(filteredControls.map(getCisControlId));
      onChange(safeSelected.filter((c) => !visibleIds.has(getCisControlId(c))));
    } else {
      onChange([]);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-slate-50 h-10 px-4 text-sm text-slate-900 focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className={selectedIds.length ? 'text-slate-900' : 'text-slate-400'}>
          {selectedIds.length > 0
            ? `CIS Controls (${selectedIds.length} Selected)`
            : 'Select CIS Controls'}
        </span>
        <ChevronDownIcon className={`h-4 w-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && !disabled && (
        <div className="absolute z-20 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 p-3">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search CIS Controls"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 h-9 pl-9 pr-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none"
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <button type="button" onClick={selectAll} className="font-medium text-[#2F2A7E] hover:underline">
                Select All
              </button>
              <button type="button" onClick={deselectAll} className="font-medium text-slate-500 hover:underline">
                Deselect All
              </button>
            </div>
          </div>

          <div className="max-h-56 overflow-y-auto py-1">
            {filteredControls.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-slate-400">No CIS Controls found.</p>
            ) : (
              filteredControls.map((control) => {
                const id = getCisControlId(control);
                const checked = selectedIds.includes(id);
                return (
                  <div
                    key={id}
                    role="checkbox"
                    aria-checked={checked}
                    tabIndex={0}
                    onClick={() => toggleControl(control)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleControl(control);
                      }
                    }}
                    className="flex cursor-pointer items-start gap-3 px-4 py-2 text-sm hover:bg-slate-50"
                  >
                    <span
                      className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                        checked ? 'border-[#2F2A7E] bg-[#2F2A7E]' : 'border-slate-300 bg-white'
                      }`}
                    >
                      {checked && <CheckIcon className="h-3 w-3 text-white" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-medium text-slate-900">{id} - {getCisControlName(control)}</span>
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Ai_CSPM = () => {
  const { user } = useAuth();
  const normalizedUserRole = (user?.role || user?.user_role || '').toString().toLowerCase();
  const isPlatformAdmin = ['platform_admin', 'admin', 'superadmin'].includes(normalizedUserRole);
  // Narrowed to exactly the roles the backend allows to create/modify
  // checks (see _can_modify_check / list_checks POST allowed_roles in
  // ai_cspm/views.py: PLATFORM_ADMIN, CUSTOMER_ADMIN, SECURITY_ANALYST).
  // "customer"/"client" were removed - the backend never allows them
  // and showing action buttons for them would always 403.
  const canManageRules =
    isPlatformAdmin ||
    [
      "customer_admin",
      "security_analyst",
    ].includes(normalizedUserRole);

  // Same real backend-allowed role set as canManageRules - used to
  // gate scan-trigger actions (trigger_scan/rescan in views.py use the
  // identical allowed_roles list).
  const canTriggerScans = canManageRules;

  const [searchParams, setSearchParams] = useSearchParams();
  const validTabIds = tabs.map((t) => t.id);
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState(
    validTabIds.includes(tabFromUrl) ? tabFromUrl : 'dashboard'
  );

  // Keeps the active tab in the URL (?tab=findings) so a refresh on
  // /ai-cspm doesn't always reset to Dashboard.
  const setActiveTab = (tabId) => {
    setActiveTabState(tabId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabId);
      return next;
    });
  };

  // The ?tab= read above only ran once, at mount (useState initializer) -
  // a navigate("/ai-cspm?tab=X") from elsewhere (e.g. a notification or
  // KPI card click) changes the URL but never remounts this component,
  // so activeTab never followed it. This keeps activeTab in sync with
  // the URL any time it changes externally, not just on first load.
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && validTabIds.includes(tab) && tab !== activeTab) {
      setActiveTabState(tab);
    }
  }, [searchParams]);

  const [loadingCSPM, setLoadingCSPM] = useState(true);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);

  const [loadingMessage, setLoadingMessage] = useState("Loading AI CSPM...");

  const [dashboardStats, setDashboardStats] = useState({});
  const [assetsByProvider, setAssetsByProvider] = useState({ aws: 0, azure: 0, gcp: 0, unknown: 0, total: 0 });

  const [assets, setAssets] = useState([]);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const [assetsPage, setAssetsPage] = useState(1);
  const [assetSearch, setAssetSearch] = useState('');
  const [assetSortBy, setAssetSortBy] = useState('last_synced_at');
  const [assetSortDir, setAssetSortDir] = useState('desc');
  const [assetLocalFilters, setAssetLocalFilters] = useState({ type: '', region: '', criticality: '', status: '', company: '' });

  const [rules, setRules] = useState([]);
  const [rulesTotal, setRulesTotal] = useState(0);
  const [rulesPage, setRulesPage] = useState(1);
  const [ruleSearch, setRuleSearch] = useState('');
  const [ruleStatusFilter, setRuleStatusFilter] = useState('');
  const [ruleCategoryFilter, setRuleCategoryFilter] = useState('');
  const [ruleLocalFilters, setRuleLocalFilters] = useState({ check_id: '', service: '', severity: '' });
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [newRule, setNewRule] = useState({
    check_id: '',
    title: '',
    description: '',
    cloud_provider: '',
    service: '',
    category: '',
    severity: '',
    cis_controls: [],
    recommendation: '',
    remediation: '',
    risk_ids: [],
    is_active: true
  });
  const [cisControlOptions, setCisControlOptions] = useState([]);
  const [viewRule, setViewRule] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ruleActionLoading, setRuleActionLoading] = useState(false);
  const [createRuleLoading, setCreateRuleLoading] = useState(false);

  const [findings, setFindings] = useState([]);
  const [findingsTotal, setFindingsTotal] = useState(0);
  const [findingsPage, setFindingsPage] = useState(1);
  const [findingSearch, setFindingSearch] = useState('');
  const [findingStatusFilter, setFindingStatusFilter] = useState('');
  const [findingLocalFilters, setFindingLocalFilters] = useState({ control_name: '', severity: '' });
  const [viewFindingRisks, setViewFindingRisks] = useState(null);
  const [showFindingRiskModal, setShowFindingRiskModal] = useState(false);
  const [findingActionLoading, setFindingActionLoading] = useState(false);

  const [ruleForOwnership, setRuleForOwnership] = useState(null);
  const [showRuleOwnershipModal, setShowRuleOwnershipModal] = useState(false);

  const [remediation, setRemediation] = useState([]);
  const [remediationTotal, setRemediationTotal] = useState(0);
  const [remediationPage, setRemediationPage] = useState(1);
  const [remediationSearch, setRemediationSearch] = useState('');
  const [remediationActionTypeFilter, setRemediationActionTypeFilter] = useState('');
  const [remediationLocalFilters, setRemediationLocalFilters] = useState({ performed_by: '', notes: '' });
  const [riskList, setRiskList] = useState([]);

  // ---- Scan Controls (Dashboard tab) ----
  const [scans, setScans] = useState([]);
  const [connectorOptions, setConnectorOptions] = useState([]);
  const [selectedConnectorId, setSelectedConnectorId] = useState('');
  const [scanTriggerLoading, setScanTriggerLoading] = useState(false);
  const [rescanLoadingId, setRescanLoadingId] = useState(null);
  const [scansLoading, setScansLoading] = useState(false);

 // ===========================
// Get JWT Token
// ===========================
const getAuthToken = () => {
  // Normal token keys
  let token =
    localStorage.getItem("accessToken") ||
    localStorage.getItem("token");

  if (token) return token;

  // AWS Cognito Access Token
  const accessTokenKey = Object.keys(localStorage).find((key) =>
    key.endsWith(".accessToken")
  );

  if (accessTokenKey) {
    console.log("Using Cognito Access Token");
    return localStorage.getItem(accessTokenKey);
  }

  // AWS Cognito ID Token (fallback)
  const idTokenKey = Object.keys(localStorage).find((key) =>
    key.endsWith(".idToken")
  );

  if (idTokenKey) {
    console.log("Using Cognito ID Token");
    return localStorage.getItem(idTokenKey);
  }

  return null;
};

// ===========================
// API Fetch
// ===========================
const apiFetch = async (endpoint, params = {}, options = {}) => {
  const method = (options.method || "GET").toUpperCase();

  const query = new URLSearchParams(params).toString();

  const url =
    `${BACKEND_URL}/api/ai-cspm${endpoint}` +
    (query ? `?${query}` : "");

  const token = getAuthToken();

  console.log("API URL:", url);
  console.log("TOKEN FOUND:", !!token);

  if (!token) {
    console.error("❌ No access token found");
    toast.error("Authentication token not found. Please login again.");
    throw new Error("Access token not available");
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  console.log("REQUEST HEADERS:", headers);

  const response = await fetch(url, {
    method,
    headers,
    body:
      method === "GET"
        ? undefined
        : JSON.stringify(options.body || {}),
  });

  console.log("STATUS:", response.status);

  if (!response.ok) {
    const errorText = await response.text();
    console.error("API ERROR:", errorText);

    let readableMessage = errorText;
    try {
      const parsed = JSON.parse(errorText);
      readableMessage =
        parsed.detail ||
        parsed.message ||
        parsed.error ||
        (Array.isArray(parsed.risk_ids) ? `Risk Mapping: ${parsed.risk_ids.join(' ')}` : null) ||
        Object.entries(parsed)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(' ') : msgs}`)
          .join(' | ') ||
        errorText;
    } catch (e) {
      // Not JSON — fall back to the raw text.
    }

    throw new Error(readableMessage || `Request failed with status ${response.status}`);
  }

  // Handle empty response (204 No Content)
  if (response.status === 204) {
    return {};
  }

  return await response.json();
};

  const loadDashboard = async (initial = false) => {

    try {

        if (initial) {
            setLoading(true);
            setLoadingMessage("Loading dashboard data...");
        } else {
            setTableLoading(true);
            setLoadingMessage("Refreshing dashboard metrics...");
        }

        const data = await apiFetch("/dashboard/stats/");

        setDashboardStats(data);

        const byProvider = await apiFetch("/dashboard/by-provider/");

        setAssetsByProvider(byProvider || { aws: 0, azure: 0, gcp: 0, unknown: 0, total: 0 });

    } catch (error) {

        toast.error("Unable to load dashboard");

    } finally {

        if (initial)
            setLoading(false);
        else
            setTableLoading(false);

    }

};

const loadAssets = async () => {

  try {

    setTableLoading(true);
    setLoadingMessage("Loading asset inventory...");

    const payload = await apiFetch("/assets/", {
      page: assetsPage,
      page_size: DEFAULT_PAGE_SIZE,
      search: assetSearch,
      sort_by: assetSortBy,
      sort_dir: assetSortDir
    });

    // 👇 ADD THESE LINES
    console.log("========== ASSETS API ==========");
    console.log(payload);
    console.log("Count:", payload.count);
    console.log("Results:", payload.results);
    console.log("===============================");

    setAssets(payload.results || []);
    setAssetsTotal(payload.count || 0);

  } catch (err) {

    console.error(err);
    toast.error("Unable to load assets");

  } finally {

    setTableLoading(false);

  }

};

  const loadRules = async () => {
  try {
    setTableLoading(true);
    setLoadingMessage("Loading security rules...");

    const params = {
      page: rulesPage,
      page_size: DEFAULT_PAGE_SIZE,
    };

    if (ruleSearch.trim()) {
      params.search = ruleSearch.trim();
    }

    if (ruleStatusFilter !== "") {
      params.is_active = ruleStatusFilter === "true";
    }

    if (ruleCategoryFilter) {
      params.category = ruleCategoryFilter;
    }

    const payload = await apiFetch("/checks/", params);

    console.log("RULE API RESPONSE:", payload);

    let data = [];

    if (Array.isArray(payload)) {
      data = payload;
    } else if (Array.isArray(payload.results)) {
      data = payload.results;
    } else if (Array.isArray(payload.data)) {
      data = payload.data;
    } else if (Array.isArray(payload.items)) {
      data = payload.items;
    }

    console.log("RULES:", data);

    setRules(data);
    setRulesTotal(payload.count || data.length);

  } catch (err) {
    console.error(err);
    toast.error("Unable to load rules");
  } finally {
    setTableLoading(false);
  }
};

  const loadFindings = async () => {
    try {
      setTableLoading(true);
      setLoadingMessage("Loading findings...");

      const payload = await apiFetch('/findings/', {
        search: findingSearch,
        status: findingStatusFilter,
        page: findingsPage,
        page_size: DEFAULT_PAGE_SIZE
      });
      console.log("FINDINGS API RESPONSE:", payload);

      setFindings(payload.results|| []);
      setFindingsTotal(payload.count || 0);
    } catch (error) {
      console.error(error);
      toast.error('Unable to load CSPM findings');
    } finally {
      setTableLoading(false);
    }
  };

  const loadRemediation = async () => {
  try {
    setTableLoading(true);
    setLoadingMessage("Loading remediation actions...");

    const payload = await apiFetch("/remediation/", {
      search: remediationSearch,
      action_type: remediationActionTypeFilter,
      page: remediationPage,
      page_size: DEFAULT_PAGE_SIZE,
    });

    console.log("REMEDIATION RESPONSE:", payload);

    setRemediation(payload.results || []);
    setRemediationTotal(payload.count || 0);
  } catch (error) {
    console.error(error);
    toast.error("Unable to load remediation recommendations");
  } finally {
    setTableLoading(false);
  }
};

  // ---- Scan Controls ----
  const loadConnectorOptions = async () => {
    try {
      const response = await apiFetch("/connectors/options/");
      const results = response?.results || [];
      setConnectorOptions(results);
      setSelectedConnectorId((prev) => prev || (results[0] && results[0].id) || '');
    } catch (err) {
      console.error("Failed to load connectors", err);
    }
  };

  const loadScans = async () => {
    try {
      setScansLoading(true);
      const response = await apiFetch("/scans/");
      setScans(response?.results || []);
    } catch (err) {
      console.error("Failed to load scans", err);
      toast.error("Unable to load scan history");
    } finally {
      setScansLoading(false);
    }
  };

  const handleTriggerScan = async () => {
    if (!selectedConnectorId) {
      toast.error("Select a connector to scan");
      return;
    }
    if (scanTriggerLoading) return;

    try {
      setScanTriggerLoading(true);
      await apiFetch("/scans/trigger/", {}, {
        method: "POST",
        body: { connector_id: selectedConnectorId }
      });
      toast.success("Scan triggered successfully");
      await loadScans();
      await loadDashboard();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Unable to trigger scan");
    } finally {
      setScanTriggerLoading(false);
    }
  };

  const handleRescan = async (scanId) => {
    if (rescanLoadingId) return;
    try {
      setRescanLoadingId(scanId);
      await apiFetch(`/scans/${scanId}/rescan/`, {}, { method: "POST" });
      toast.success("Rescan queued");
      await loadScans();
      await loadDashboard();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Unable to queue rescan");
    } finally {
      setRescanLoadingId(null);
    }
  };

  // Platform-Admin-only (see update_finding_status in ai_cspm/views.py) -
  // only invoked from a button gated on finding.can_edit && isPlatformAdmin.
  const handleResolveFinding = async (finding) => {
    if (findingActionLoading) return;
    try {
      setFindingActionLoading(true);
      await apiFetch(`/findings/${finding.id}/status/`, {}, {
        method: 'PATCH',
        body: { status: 'resolved' }
      });
      toast.success('Finding marked as resolved');
      await loadFindings();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Unable to update finding status');
    } finally {
      setFindingActionLoading(false);
    }
  };

  const loadRisks = async () => {
  try {
    const response = await apiFetch("/risks/");

    console.log("RISKS API RESPONSE:", response);

    setRiskList(response || []);
  } catch (err) {
    console.error("Failed to load risks", err);
    toast.error("Unable to load risks");
  }
};

  // CSPM-relevant CIS Controls v8, sourced from the Trust Center
  // Framework via the backend - never hardcoded here.
  const loadCisControls = async () => {
  try {
    const response = await apiFetch("/checks/cis-controls/");

    setCisControlOptions(Array.isArray(response) ? response : []);
  } catch (err) {
    console.error("Failed to load CIS Controls", err);
    toast.error("Unable to load CIS Controls");
  }
};

  // Normalize a rule's mapped risks into an array of risk IDs, whether the
  // backend returns `risk_ids`, a `risks` array of objects, or the legacy
  // single `risk_id` field.
  const getRuleRiskIds = (rule) => {
    if (!rule) return [];
    if (Array.isArray(rule.risk_ids)) return rule.risk_ids;
    if (Array.isArray(rule.risks)) {
      return rule.risks.map((r) => (typeof r === 'string' ? r : getRiskId(r)));
    }
    if (rule.risk_id) return [rule.risk_id];
    return [];
  };

  // Resolve full risk records (id, name, framework, description) for a rule,
  // preferring data already embedded on the rule and falling back to riskList.
  const getRuleMappedRisks = (rule) => {
    const ids = getRuleRiskIds(rule);
    if (Array.isArray(rule?.risks) && rule.risks.length && typeof rule.risks[0] !== 'string') {
      return rule.risks;
    }
    return ids.map((id) => riskList.find((r) => getRiskId(r) === id) || { risk_id: id });
  };

  // Same idea as getRuleRiskIds/getRuleMappedRisks, but for a finding -
  // the findings API currently returns a single `risk_id` (plus a
  // `risk_mapping` framework-mappings array), so normalize whichever
  // shape shows up into a list of ids and resolve full details via the
  // already-loaded riskList.
  const getFindingRiskIds = (finding) => {
    if (!finding) return [];
    if (Array.isArray(finding.risk_ids)) return finding.risk_ids.filter(Boolean);
    if (finding.risk_id) return [finding.risk_id];
    return [];
  };

  const getFindingMappedRisks = (finding) => {
    const ids = getFindingRiskIds(finding);
    return ids.map((id) => riskList.find((r) => getRiskId(r) === id) || { risk_id: id });
  };

  const openFindingRiskModal = (finding) => {
    setViewFindingRisks(finding);
    setShowFindingRiskModal(true);
  };

  const closeFindingRiskModal = () => {
    setViewFindingRisks(null);
    setShowFindingRiskModal(false);
  };

  const openRuleOwnershipModal = (rule) => {
    setRuleForOwnership(rule);
    setShowRuleOwnershipModal(true);
  };

  const closeRuleOwnershipModal = () => {
    setRuleForOwnership(null);
    setShowRuleOwnershipModal(false);
  };

  const formatOwnershipDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${day}-${month}-${year} ${timePart}`;
  };

  const openViewModal = (rule) => {
    setViewRule(rule);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setViewRule(null);
    setShowViewModal(false);
  };

  const openEditModal = (rule) => {
    setEditRule({ ...rule, risk_ids: getRuleRiskIds(rule) });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setEditRule(null);
    setShowEditModal(false);
  };

  const openDeleteModal = (rule) => {
    setRuleToDelete(rule);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setRuleToDelete(null);
    setShowDeleteModal(false);
  };

  const handleRuleSearch = () => {
    setRulesPage(1);
    loadRules();
  };

  const openAddRuleModal = () => {
    setNewRule({
      check_id: '',
      title: '',
      description: '',
      cloud_provider: '',
      service: '',
      category: '',
      severity: '',
      cis_controls: [],
      recommendation: '',
      remediation: '',
      risk_ids: [],
      is_active: true
    });
    setShowAddRuleModal(true);
  };

  const closeAddRuleModal = () => {
    setShowAddRuleModal(false);
  };

  const handleNewRuleFieldChange = (field, value) => {
    setNewRule((prev) => ({ ...prev, [field]: value }));
  };

  const isCreateRuleValid =
    newRule.check_id.trim() !== "" &&
    newRule.title.trim() !== "" &&
    newRule.description.trim() !== "" &&
    newRule.cloud_provider.trim() !== "" &&
    newRule.service.trim() !== "" &&
    newRule.category.trim() !== "" &&
    newRule.severity.trim() !== "";



  const handleCreateRule = async () => {
    if (!newRule.check_id.trim() || !newRule.title.trim() || !newRule.description.trim() || !newRule.cloud_provider.trim() || !newRule.service.trim() || !newRule.category.trim() || !newRule.severity.trim()) {
      toast.error('Please fill in all required rule fields');
      return;
    }

    try {
      setCreateRuleLoading(true);
      const payload = {
        ...newRule,
        risk_ids: newRule.risk_ids || [],
        cis_controls: newRule.cis_controls || [],
      };
      console.log('CREATE RULE PAYLOAD', payload);
      await apiFetch('/checks/', {}, {
        method: 'POST',
        body: payload
      });
      toast.success('Rule created successfully');
      setShowAddRuleModal(false);
      await loadRules();
    } catch (error) {
      console.error(error);
      const backendMessage =
        (error && error.message && error.message.trim()) ||
        'Unable to create rule. Please try again.';
      toast.error(backendMessage);
    } finally {
      setCreateRuleLoading(false);
    }
  };

  const handleEditFieldChange = (field, value) => {
    setEditRule((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleUpdateRule = async () => {
    if (!editRule) return;

    const ruleIdentifier = editRule.check_id;
    if (!ruleIdentifier) {
      console.error('Unable to update rule: missing identifier', editRule);
      toast.error('Unable to update rule: missing identifier');
      return;
    }

    const payload = {
      title: editRule.title,
      description: editRule.description,
      category: editRule.category,
      severity: editRule.severity,
      is_active: editRule.is_active,

      cis_controls: editRule.cis_controls || [],
      recommendation: editRule.recommendation,
      remediation: editRule.remediation,
      risk_ids: editRule.risk_ids || []
    };
    console.log('PATCH URL:', `/checks/${ruleIdentifier}/`);
    console.log('PATCH PAYLOAD:', payload);

    try {
      setRuleActionLoading(true);
      await apiFetch(`/checks/${encodeURIComponent(ruleIdentifier)}/`, {}, {
        method: 'PATCH',
        body: payload
      });

      toast.success('Rule updated successfully');
      closeEditModal();
      await loadRules();
    } catch (error) {
      console.error('Failed to update rule:', error);
      const backendMessage =
        (error && error.message && error.message.trim()) ||
        'Unable to update rule. Please try again.';
      toast.error(backendMessage);
    } finally {
      setRuleActionLoading(false);
    }
  };

  const handleDeleteRule = async () => {
    if (!ruleToDelete) return;

    try {
      setRuleActionLoading(true);
      const deleteUrl = `/checks/${encodeURIComponent(ruleToDelete.check_id)}/`;
      console.log('DELETE URL', deleteUrl);
      await apiFetch(deleteUrl, {}, {
        method: 'DELETE'
      });
      toast.success('Rule deleted successfully');
      closeDeleteModal();
      await loadRules();
    } catch (error) {
      console.error(error);
      toast.error('Unable to delete rule');
    } finally {
      setRuleActionLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;

    const init = async () => {

        // Show "Loading AI CSPM..."
        setLoadingMessage("Loading AI CSPM...");
        setLoadingCSPM(true);

        // Small delay so user can actually see it
        await new Promise(resolve => setTimeout(resolve, 800));

        setLoadingCSPM(false);

        // Show "Loading dashboard data..."
        setLoadingMessage("Loading dashboard data...");

        await loadDashboard(true);
    };

    init();

}, [user]);

  useEffect(() => {
    if (!user) return;

    loadRisks();
    loadCisControls();

    switch(activeTab){

        case "assets":
            loadAssets();
            break;

        case "rules":
            loadRules();
            break;

        case "findings":
            loadFindings();
            break;

        case "remediation":
            loadRemediation();
            break;

        case "dashboard":
            if (canTriggerScans) loadConnectorOptions();
            loadScans();
            break;

        default:
            break;
    }

},[
activeTab,

assetsPage,
assetSearch,
assetSortBy,
assetSortDir,

rulesPage,
ruleSearch,
ruleStatusFilter,
ruleCategoryFilter,

findingsPage,
findingSearch,
findingStatusFilter,

remediationPage,
remediationSearch,
remediationActionTypeFilter
]);

  const formatDate = (value) => {
    if (!value) return 'N/A';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };



  const renderAssetTable = () => {
    const displayedAssets = assets.filter((asset) => {
      if (assetLocalFilters.company && asset.company_name !== assetLocalFilters.company) return false;
      if (assetLocalFilters.type && (asset.type || asset.asset_type) !== assetLocalFilters.type) return false;
      if (assetLocalFilters.region && (asset.region || '-') !== assetLocalFilters.region) return false;
      if (assetLocalFilters.criticality && (asset.criticality || 'Unknown') !== assetLocalFilters.criticality) return false;
      if (assetLocalFilters.status && asset.connection_status !== assetLocalFilters.status) return false;
      return true;
    });

    const uniqueTypes = [...new Set(assets.map((a) => a.type || a.asset_type).filter(Boolean))];
    const uniqueRegions = [...new Set(assets.map((a) => a.region || '-').filter(Boolean))];
    const uniqueCriticalities = [...new Set(assets.map((a) => a.criticality || 'Unknown').filter(Boolean))];
    const uniqueStatuses = [...new Set(assets.map((a) => a.connection_status).filter(Boolean))];
    const uniqueCompanies = [...new Set(assets.map((a) => a.company_name).filter(Boolean))];

    const columns = [
      ...(isPlatformAdmin
        ? [
            {
              id: 'company_name',
              accessorFn: (a) => a.company_name,
              header: 'Company',
              enableSorting: false,
              meta: {
                filter: {
                  variant: 'select',
                  label: 'Company',
                  placeholder: 'All companies',
                  options: uniqueCompanies.map((v) => ({ value: v, label: v })),
                },
              },
              cell: ({ row }) => <span className="text-slate-900 font-medium">{row.original.company_name || '-'}</span>,
            },
          ]
        : []),
      {
        id: 'connector_name',
        accessorFn: (a) => a.connector_name,
        header: 'Connector Name',
        enableSorting: false,
        meta: { filter: { variant: 'text', label: 'Connector Name', placeholder: 'Search connector' } },
        cell: ({ row }) => <span className="text-slate-900">{row.original.connector_name}</span>,
      },
      {
        id: 'type',
        accessorFn: (a) => a.type || a.asset_type,
        header: 'Type',
        enableSorting: false,
        meta: {
          filter: {
            variant: 'select',
            label: 'Type',
            placeholder: 'All types',
            options: uniqueTypes.map((v) => ({ value: v, label: v })),
          },
        },
        cell: ({ row }) => <span className="text-slate-700">{row.original.type || row.original.asset_type}</span>,
      },
      {
        id: 'region',
        accessorFn: (a) => a.region || '-',
        header: 'Region',
        enableSorting: false,
        meta: {
          filter: {
            variant: 'select',
            label: 'Region',
            placeholder: 'All regions',
            options: uniqueRegions.map((v) => ({ value: v, label: v })),
          },
        },
        cell: ({ row }) => <span className="text-slate-700">{row.original.region || '-'}</span>,
      },
      {
        id: 'criticality',
        accessorFn: (a) => a.criticality || 'Unknown',
        header: 'Criticality',
        enableSorting: false,
        meta: {
          filter: {
            variant: 'select',
            label: 'Criticality',
            placeholder: 'All criticalities',
            options: uniqueCriticalities.map((v) => ({ value: v, label: v })),
          },
        },
        cell: ({ row }) => <span className="text-slate-700">{row.original.criticality || 'Unknown'}</span>,
      },
      {
        id: 'connection_status',
        accessorFn: (a) => a.connection_status,
        header: 'Connection Status',
        enableSorting: false,
        meta: {
          filter: {
            variant: 'select',
            label: 'Connection Status',
            placeholder: 'All statuses',
            options: uniqueStatuses.map((v) => ({ value: v, label: v })),
          },
        },
        cell: ({ row }) => (
          <span
            className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
              row.original.connection_status === 'Connected'
                ? 'bg-green-100 text-green-700 border-green-200'
                : 'bg-red-100 text-red-700 border-red-200'
            }`}
          >
            {row.original.connection_status}
          </span>
        ),
      },
      {
        id: 'last_synced_at',
        accessorFn: (a) => a.last_synced_at,
        header: 'Last Synced',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-slate-700">
            {row.original.last_synced_at ? formatDate(row.original.last_synced_at) : 'N/A'}
          </span>
        ),
      },
    ];

    const columnFilters = [
      assetSearch ? { id: 'connector_name', value: assetSearch } : null,
      assetLocalFilters.type ? { id: 'type', value: assetLocalFilters.type } : null,
      assetLocalFilters.region ? { id: 'region', value: assetLocalFilters.region } : null,
      assetLocalFilters.criticality ? { id: 'criticality', value: assetLocalFilters.criticality } : null,
      assetLocalFilters.status ? { id: 'connection_status', value: assetLocalFilters.status } : null,
      assetLocalFilters.company ? { id: 'company_name', value: assetLocalFilters.company } : null,
    ].filter(Boolean);

    const handleColumnFiltersChange = (updater) => {
      const next = typeof updater === 'function' ? updater(columnFilters) : updater;
      const byId = Object.fromEntries(next.map((f) => [f.id, f.value]));
      setAssetSearch(byId.connector_name || '');
      setAssetLocalFilters({
        type: byId.type || '',
        region: byId.region || '',
        criticality: byId.criticality || '',
        status: byId.connection_status || '',
        company: byId.company_name || '',
      });
      setAssetsPage(1);
    };

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-end gap-3">
          <button
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => {
              setAssetSortDir(prev => prev === "desc" ? "asc" : "desc");
            }}
          >
            <span className="inline-flex items-center gap-1">
              Sort: {assetSortBy.replace('_', ' ')}
              {assetSortDir === 'desc' ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronUpIcon className="h-4 w-4" />
              )}
            </span>
          </button>
          <select
            value={assetSortBy}
            onChange={(e) => { setAssetSortBy(e.target.value); setAssetsPage(1);}}
            className="rounded-xl h-10 px-5 text-sm font-medium border border-slate-200 bg-white h-10 px-4 text-sm text-slate-900"
          >
            <option value="last_synced_at">Last Synced</option>
            <option value="connector_name">Connector Name</option>
            <option value="connector_type">Connector Type</option>
            <option value="criticality">Criticality</option>
          </select>
        </div>

        <DataTable
          columns={columns}
          data={displayedAssets}
          getRowId={(a, i) => String(a.id ?? i)}
          loading={tableLoading}
          manualPagination
          rowCount={assetsTotal}
          pagination={{ pageIndex: (assetsPage || 1) - 1, pageSize: DEFAULT_PAGE_SIZE }}
          onPaginationChange={(updater) => {
            const current = { pageIndex: (assetsPage || 1) - 1, pageSize: DEFAULT_PAGE_SIZE };
            const next = typeof updater === 'function' ? updater(current) : updater;
            setAssetsPage(next.pageIndex + 1);
          }}
          enableColumnFilters
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          emptyMessage="No asset connectors found."
          enableColumnVisibility={false}
          enableColumnResizing
          maxBodyHeight="65vh"
        />
      </div>
    );
  };

  const categories = useMemo(() => {
    return [...new Set(rules.map((item) => item.category || 'Uncategorized'))].sort();
  }, [rules]);

  if (loadingCSPM) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <ApiLoader
                variant="spinner"
                size={50}
                label="Loading AI CSPM..."
            />
        </div>
    );
}

if (loading) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <ApiLoader
                variant="spinner"
                size={50}
                label="Loading dashboard data..."
            />
        </div>
    );
}

  const renderRules = () => (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Rules</h2>
            {!canManageRules && (
              <p className="mt-2 text-sm text-slate-500">You can view rules, but only platform admins can create or edit them.</p>
            )}
          </div>
          
          {canManageRules && (
            <button
              type="button"
              onClick={openAddRuleModal}
              className="inline-flex items-center gap-2 rounded-xl bg-[#2F2A7E] px-4 py-2 text-sm font-medium text-white hover:bg-[#27226B]"
            >
              + Add Rule
            </button>
          )}
          
        </div>
        
      </div>
      {renderRulesTable()}
    </div>
  );

  const renderRulesTable = () => {
    const displayedRules = rules.filter((r) => {
      if (ruleLocalFilters.check_id && !String(r.check_id || '').toLowerCase().includes(ruleLocalFilters.check_id.toLowerCase())) return false;
      if (ruleLocalFilters.service && r.service !== ruleLocalFilters.service) return false;
      if (ruleLocalFilters.severity && r.severity !== ruleLocalFilters.severity) return false;
      return true;
    });

    const uniqueServices = [...new Set(rules.map((r) => r.service).filter(Boolean))];
    const uniqueSeverities = [...new Set(rules.map((r) => r.severity).filter(Boolean))];

    const ruleColumns = [
      {
        id: 'check_id',
        accessorFn: (r) => r.check_id,
        header: 'Check ID',
        enableSorting: false,
        meta: { filter: { variant: 'text', label: 'Check ID', placeholder: 'Search check ID' } },
        cell: ({ row }) => <span className="text-slate-900">{row.original.check_id}</span>,
      },
      {
        id: 'title',
        accessorFn: (r) => r.title,
        header: 'Check Title',
        enableSorting: false,
        meta: { filter: { variant: 'text', label: 'Title', placeholder: 'Search check ID, title, service, category' } },
        cell: ({ row }) => <span className="text-slate-700">{row.original.title}</span>,
      },
      {
        id: 'scope',
        header: 'Scope',
        enableSorting: false,
        cell: ({ row }) => {
          const rule = row.original;
          return (
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                  rule.scope === 'Platform' ? 'bg-[#2F2A7E]/10 text-[#2F2A7E]' : 'bg-teal-100 text-teal-800'
                }`}
              >
                {rule.scope || (rule.is_global ? 'Platform' : 'Customer')}
              </span>
              {rule.scope !== 'Platform' && (
                <button
                  type="button"
                  onClick={() => openRuleOwnershipModal(rule)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                  aria-label="View rule scope details"
                >
                  <Eye className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        },
      },
      {
        id: 'category',
        accessorFn: (r) => r.category,
        header: 'Category',
        enableSorting: false,
        meta: {
          filter: {
            variant: 'select',
            label: 'Category',
            placeholder: 'All categories',
            options: categories.map((c) => ({ value: c, label: c })),
          },
        },
        cell: ({ row }) => <span className="text-slate-700">{row.original.category || 'N/A'}</span>,
      },
      {
        id: 'service',
        accessorFn: (r) => r.service,
        header: 'Cloud Service',
        enableSorting: false,
        meta: {
          filter: {
            variant: 'select',
            label: 'Cloud Service',
            placeholder: 'All services',
            options: uniqueServices.map((v) => ({ value: v, label: v })),
          },
        },
        cell: ({ row }) => <span className="text-slate-700">{row.original.service || 'N/A'}</span>,
      },
      {
        id: 'severity',
        accessorFn: (r) => r.severity,
        header: 'Severity',
        enableSorting: false,
        meta: {
          filter: {
            variant: 'select',
            label: 'Severity',
            placeholder: 'All severities',
            options: uniqueSeverities.map((v) => ({ value: v, label: v })),
          },
        },
        cell: ({ row }) => <span className="text-slate-700">{row.original.severity || 'N/A'}</span>,
      },
      {
        id: 'cis_controls',
        header: 'CIS Controls',
        enableSorting: false,
        cell: ({ row }) => {
          const controls = Array.isArray(row.original.cis_controls) ? row.original.cis_controls : [];
          if (!controls.length) {
            return <span className="text-slate-400">Not Mapped</span>;
          }
          const visible = controls.slice(0, 3);
          const remaining = controls.length - visible.length;
          return (
            <div className="flex flex-wrap items-center gap-1">
              {visible.map((c) => (
                <span
                  key={getCisControlId(c)}
                  className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700"
                >
                  {getCisControlId(c)}
                </span>
              ))}
              {remaining > 0 && (
                <span className="inline-flex items-center rounded-full bg-[#2F2A7E]/10 px-2 py-0.5 text-xs font-medium text-[#2F2A7E]">
                  +{remaining} More
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'is_active',
        accessorFn: (r) => (r.is_active ? 'true' : 'false'),
        header: 'Status',
        enableSorting: false,
        meta: {
          filter: {
            variant: 'select',
            label: 'Status',
            placeholder: 'All statuses',
            options: [
              { value: 'true', label: 'Enabled' },
              { value: 'false', label: 'Disabled' },
            ],
          },
        },
        cell: ({ row }) => (
          <span
            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
              row.original.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {row.original.is_active ? 'Enabled' : 'Disabled'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          const rule = row.original;
          return (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openViewModal(rule)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                aria-label="View rule"
              >
                <Eye className="h-4 w-4" />
              </button>
              {(rule.can_edit ?? canManageRules) && (
                <button
                  type="button"
                  onClick={() => openEditModal(rule)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                  aria-label="Edit rule"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
              {(rule.can_delete ?? canManageRules) && (
                <button
                  type="button"
                  onClick={() => openDeleteModal(rule)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                  aria-label="Delete rule"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        },
      },
    ];

    const ruleColumnFilters = [
      ruleSearch ? { id: 'title', value: ruleSearch } : null,
      ruleCategoryFilter ? { id: 'category', value: ruleCategoryFilter } : null,
      ruleStatusFilter ? { id: 'is_active', value: ruleStatusFilter } : null,
      ruleLocalFilters.check_id ? { id: 'check_id', value: ruleLocalFilters.check_id } : null,
      ruleLocalFilters.service ? { id: 'service', value: ruleLocalFilters.service } : null,
      ruleLocalFilters.severity ? { id: 'severity', value: ruleLocalFilters.severity } : null,
    ].filter(Boolean);

    const handleRuleColumnFiltersChange = (updater) => {
      const next = typeof updater === 'function' ? updater(ruleColumnFilters) : updater;
      const byId = Object.fromEntries(next.map((f) => [f.id, f.value]));
      setRuleSearch(byId.title || '');
      setRuleCategoryFilter(byId.category || '');
      setRuleStatusFilter(byId.is_active || '');
      setRuleLocalFilters({
        check_id: byId.check_id || '',
        service: byId.service || '',
        severity: byId.severity || '',
      });
      setRulesPage(1);
    };

    return (
      <div>
        <DataTable
          columns={ruleColumns}
          data={displayedRules}
          getRowId={(r, i) => String(r.check_id || i)}
          loading={tableLoading}
          manualPagination
          rowCount={rulesTotal}
          pagination={{ pageIndex: (rulesPage || 1) - 1, pageSize: DEFAULT_PAGE_SIZE }}
          onPaginationChange={(updater) => {
            const current = { pageIndex: (rulesPage || 1) - 1, pageSize: DEFAULT_PAGE_SIZE };
            const next = typeof updater === 'function' ? updater(current) : updater;
            setRulesPage(next.pageIndex + 1);
          }}
          enableColumnFilters
          manualFiltering
          columnFilters={ruleColumnFilters}
          onColumnFiltersChange={handleRuleColumnFiltersChange}
          emptyMessage="No rules found."
          enableColumnVisibility={false}
          enableColumnResizing
          maxBodyHeight="65vh"
        />
      </div>
    );
  };

  const renderViewRuleModal = () => {
    if (!showViewModal || !viewRule) return null;

    const mappedRisks = getRuleMappedRisks(viewRule);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
        <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-lg">
          <ModalHeader
            title="Rule Details"
            subtitle="View the selected CSPM rule."
            onClose={closeViewModal}
          />
          <div className="space-y-5 px-6 py-6 text-sm text-slate-700">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Check ID</p>
                <p className="mt-2 text-slate-900">{viewRule.check_id}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Cloud Provider</p>
                <p className="mt-2 text-slate-900">{viewRule.cloud_provider || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Check Title</p>
                <p className="mt-2 text-slate-900">{viewRule.title}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Service</p>
                <p className="mt-2 text-slate-900">{viewRule.service || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Scope</p>
                <p className="mt-2">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                      viewRule.scope === 'Platform'
                        ? 'bg-[#2F2A7E]/10 text-[#2F2A7E]'
                        : 'bg-teal-100 text-teal-800'
                    }`}
                  >
                    {(viewRule.scope || (viewRule.is_global ? 'Platform' : 'Customer'))}{' Check'}
                  </span>
                </p>
                {isPlatformAdmin && viewRule.scope !== 'Platform' && viewRule.customer_id && (
                  <p className="mt-2 text-xs text-slate-500">
                    Owning customer: <span className="font-medium text-slate-700">{viewRule.customer_id}</span>
                  </p>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Description</p>
              <p className="mt-2 whitespace-pre-wrap text-slate-900">{viewRule.description || 'N/A'}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Category</p>
                <p className="mt-2 text-slate-900">{viewRule.category || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Severity</p>
                <p className="mt-2 text-slate-900">{viewRule.severity || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Status</p>
                <p className="mt-2">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                      viewRule.is_active
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {viewRule.is_active ? "Enabled" : "Disabled"}
                  </span>
                </p>
              </div>
            </div>

            {/* Mapped CIS Controls v8 */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                Mapped CIS Controls
              </p>

              {!Array.isArray(viewRule.cis_controls) || viewRule.cis_controls.length === 0 ? (
                <p className="mt-2 text-slate-400">Not Mapped</p>
              ) : (
                <div className="mt-2 space-y-1.5">
                  {viewRule.cis_controls.map((control) => (
                    <div key={getCisControlId(control)} className="flex items-center gap-2 text-slate-900">
                      <CheckIcon className="h-4 w-4 flex-shrink-0 text-green-600" />
                      <span>{getCisControlId(control)} - {getCisControlName(control)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Risk Details (mapped-risk count shown here instead of a separate Risk Mapped field) */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                Risk Details ({mappedRisks.length} {mappedRisks.length === 1 ? "Risk" : "Risks"} Mapped)
              </p>

              {mappedRisks.length === 0 ? (
                <p className="mt-2 text-slate-400">Not Mapped</p>
              ) : (
                <div className="mt-2 max-h-72 overflow-y-auto pr-1 space-y-3">
                  {mappedRisks.map((risk) => (
                    <div
                      key={getRiskId(risk)}
                      className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Risk ID</p>
                          <p className="mt-1 text-slate-900">{getRiskId(risk) || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Risk Name</p>
                          <p className="mt-1 text-slate-900">{getRiskName(risk) || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Regulatory / Framework Mapping</p>
                          <p className="mt-1 text-slate-900">{getRiskFramework(risk) || 'N/A'}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Risk Description</p>
                          <p className="mt-1 whitespace-pre-wrap text-slate-900">{getRiskDescription(risk) || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end rounded-b-2xl border-t border-slate-200 px-6 py-4">
            <button
              type="button"
              onClick={closeViewModal}
              className="rounded-xl h-10 px-5 text-sm font-medium bg-[#2F2A7E] px-4 py-2 text-sm font-medium text-white hover:bg-[#27226B]"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFindingRiskModal = () => {
    if (!showFindingRiskModal || !viewFindingRisks) return null;

    const mappedRisks = getFindingMappedRisks(viewFindingRisks);
    const mappedControls = Array.isArray(viewFindingRisks.cis_controls) ? viewFindingRisks.cis_controls : [];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
        <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-lg">
          <ModalHeader
            title="Mapped Risks & Controls"
            subtitle={viewFindingRisks.control_name || viewFindingRisks.control_id}
            onClose={closeFindingRiskModal}
          />
          <div className="space-y-6 px-6 py-6 text-sm text-slate-700">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Mapped Risks</p>
              {mappedRisks.length === 0 ? (
                <p className="text-slate-500">No risks mapped.</p>
              ) : (
                <div className="space-y-4">
                  {mappedRisks.map((risk, idx) => (
                    <div
                      key={getRiskId(risk) || idx}
                      className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                    >
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Risk ID</p>
                          <p className="mt-1 text-slate-900">{getRiskId(risk) || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Risk Name</p>
                          <p className="mt-1 text-slate-900">{getRiskName(risk) || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Category</p>
                          <p className="mt-1 text-slate-900">{risk.category || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Severity</p>
                          <p className="mt-1 text-slate-900">{risk.severity || risk.risk_severity || 'N/A'}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Description</p>
                          <p className="mt-1 whitespace-pre-wrap text-slate-900">{getRiskDescription(risk) || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Mapped CIS / Framework Controls
                <span className="ml-2 normal-case font-normal text-slate-400">(automatically assigned)</span>
              </p>
              {mappedControls.length === 0 ? (
                <p className="text-slate-500">No controls mapped yet.</p>
              ) : (
                <div className="space-y-3">
                  {mappedControls.map((control, idx) => (
                    <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Framework</p>
                          <p className="mt-1 text-slate-900">{control.framework || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Control ID</p>
                          <p className="mt-1 text-slate-900">{control.control_id || control.control || 'N/A'}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Control Name</p>
                          <p className="mt-1 text-slate-900">{control.control_name || control.control_title || 'N/A'}</p>
                        </div>
                        {control.benchmark && (
                          <div className="sm:col-span-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Benchmark</p>
                            <p className="mt-1 text-slate-900">
                              {control.benchmark}{control.benchmark_version ? ` (v${control.benchmark_version})` : ''}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end rounded-b-2xl border-t border-slate-200 px-6 py-4">
            <button
              type="button"
              onClick={closeFindingRiskModal}
              className="rounded-xl h-10 px-5 text-sm font-medium bg-[#2F2A7E] px-4 py-2 text-sm font-medium text-white hover:bg-[#27226B]"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRuleOwnershipModal = () => {
    if (!showRuleOwnershipModal || !ruleForOwnership) return null;

    const rule = ruleForOwnership;
    const scopeLabel = rule.scope || (rule.is_global ? 'Platform' : 'Customer');
    const isPlatformScope = scopeLabel === 'Platform';
    const createdByDisplay = isPlatformScope
      ? 'Platform Admin'
      : (rule.created_by_name || rule.created_by || 'N/A');

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
        <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-lg">
          <ModalHeader
            title="Rule Scope Details"
            subtitle={rule.title || rule.check_id}
            onClose={closeRuleOwnershipModal}
          />
          <div className="space-y-4 px-6 py-6 text-sm text-slate-700">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Scope</p>
                  <p className="mt-1">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        isPlatformScope
                          ? 'bg-[#2F2A7E]/10 text-[#2F2A7E]'
                          : 'bg-teal-100 text-teal-800'
                      }`}
                    >
                      {scopeLabel}
                    </span>
                  </p>
                </div>
                <div />

                {!isPlatformScope && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Customer Name</p>
                    <p className="mt-1 text-slate-900">{rule.customer_name || 'N/A'}</p>
                  </div>
                )}

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Created By</p>
                  <p className="mt-1 text-slate-900">{createdByDisplay}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Created Date</p>
                  <p className="mt-1 text-slate-900">{formatOwnershipDate(rule.created_at)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Last Updated</p>
                  <p className="mt-1 text-slate-900">{formatOwnershipDate(rule.updated_at)}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end rounded-b-2xl border-t border-slate-200 px-6 py-4">
            <button
              type="button"
              onClick={closeRuleOwnershipModal}
              className="rounded-xl h-10 px-5 text-sm font-medium bg-[#2F2A7E] px-4 py-2 text-sm font-medium text-white hover:bg-[#27226B]"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEditRuleModal = () => {
  if (!showEditModal || !editRule) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      {ruleActionLoading && (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 rounded-xl">
        <ApiLoader
            variant="spinner"
            size={50}
            label="Saving changes..."
        />
    </div>
)}
      <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl">

        {/* Header */}
        <ModalHeader
          title="Edit Rule"
          subtitle="Update rule metadata and status."
          onClose={closeEditModal}
        />

        <div className="space-y-4 p-5">

          {/* Top */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Check ID
              </label>
              <div className="mt-1 text-slate-900">{editRule.check_id}</div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Cloud Provider
              </label>
              <div className="mt-1 text-slate-900">
                {editRule.cloud_provider || "N/A"}
              </div>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block mb-1 text-sm font-medium">
              Title
            </label>

            <input
              value={editRule.title || ""}
              onChange={(e) =>
                handleEditFieldChange("title", e.target.value)
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block mb-1 text-sm font-medium">
              Description
            </label>

            <textarea
              rows={3}
              value={editRule.description || ""}
              onChange={(e) =>
                handleEditFieldChange("description", e.target.value)
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
            />
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            <div>
              <label className="block mb-1 text-sm font-medium">
                Category
              </label>

              <input
                value={editRule.category || ""}
                onChange={(e) =>
                  handleEditFieldChange("category", e.target.value)
                }
                className="w-full rounded-lg border px-3 py-2"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium">
                Severity
              </label>

              <select
                value={editRule.severity || ""}
                onChange={(e) =>
                  handleEditFieldChange("severity", e.target.value)
                }
                className="w-full rounded-lg border px-3 py-2"
              >
                <option value="">Select</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
                <option value="informational">Informational</option>
              </select>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium">
                Status
              </label>

              <select
                value={editRule.is_active ? "true" : "false"}
                onChange={(e) =>
                  handleEditFieldChange(
                    "is_active",
                    e.target.value === "true"
                  )
                }
                className="w-full rounded-lg border px-3 py-2"
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block mb-1 text-sm font-medium">
                CIS Controls
              </label>

              <CisControlsMultiSelectDropdown
                controlList={cisControlOptions}
                selected={editRule.cis_controls || []}
                onChange={(controls) => handleEditFieldChange("cis_controls", controls)}
              />
            </div>

          </div>

          {/* Recommendation & Remediation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div>
              <label className="block mb-1 text-sm font-medium">
                Recommendation
              </label>

              <textarea
                rows={2}
                value={editRule.recommendation || ""}
                onChange={(e) =>
                  handleEditFieldChange(
                    "recommendation",
                    e.target.value
                  )
                }
                className="w-full rounded-lg border px-3 py-2"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium">
                Remediation
              </label>

              <textarea
                rows={2}
                value={editRule.remediation || ""}
                onChange={(e) =>
                  handleEditFieldChange(
                    "remediation",
                    e.target.value
                  )
                }
                className="w-full rounded-lg border px-3 py-2"
              />
            </div>

          </div>

        </div>

         {/* Risk Mapping */}
        <div className="px-5 pb-5">
          <label className="block mb-1 text-sm font-medium text-slate-700">
            Risk Mapping
          </label>

          <RiskMultiSelectDropdown
            riskList={riskList}
            selectedIds={editRule.risk_ids || []}
            onChange={(ids) => handleEditFieldChange("risk_ids", ids)}
            disabled={ruleActionLoading}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 rounded-b-2xl border-t border-slate-200 px-5 py-4">

          <button
            onClick={closeEditModal}
            className="rounded-xl h-10 px-5 text-sm font-medium border border-slate-300 px-5 py-2 hover:bg-slate-100"
          >
            Cancel
          </button>

          <button
    type="button"
    onClick={handleUpdateRule}
    disabled={ruleActionLoading}
    className="inline-flex items-center justify-center rounded-lg bg-[#2F2A7E] px-4 py-2 text-white disabled:opacity-50"
>
    {ruleActionLoading ? (
        <>
            <svg
                className="mr-2 h-4 w-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
            >
                <circle
                    cx="12"

                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    className="opacity-25"
                />
                <path
                    fill="currentColor"
                    className="opacity-75"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
            </svg>

            Saving Changes...
        </>
    ) : (
        "Save Changes"
    )}
</button>

        </div>

      </div>
    </div>
  );
};

  const renderDeleteRuleModal = () => {
    if (!showDeleteModal || !ruleToDelete) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
        <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl">
          <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Delete Rule</h2>
              <p className="mt-2 text-sm text-slate-500">Are you sure you want to delete this rule?</p>
            </div>
            <button
              type="button"
              onClick={closeDeleteModal}
              className="text-sm font-medium text-slate-500 hover:text-slate-900"
            >
              Cancel
            </button>
          </div>
          <div className="space-y-4 px-6 py-6 text-sm text-slate-700">
            <p>
              <span className="font-semibold text-slate-900">{ruleToDelete.title}</span>
              <span className="ml-2 text-slate-500">({ruleToDelete.check_id})</span>
            </p>
            <p className="text-slate-500">This action cannot be undone.</p>
          </div>
          <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
            <button
              type="button"
              onClick={closeDeleteModal}
              className="rounded-xl h-10 px-5 text-sm font-medium border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteRule}
              disabled={ruleActionLoading}
              className="inline-flex items-center justify-center rounded-xl h-10 px-5 text-sm font-medium bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAddRuleModal = () => {
    if (!showAddRuleModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
        <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
          <ModalHeader
            title="Create CSPM Rule"
            subtitle="Add a new cloud security posture management rule."
            onClose={closeAddRuleModal}
          />
          <div className="space-y-5 px-6 py-6 text-sm text-slate-700">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Check ID</label>
                <input
                  value={newRule.check_id}
                  onChange={(e) => handleNewRuleFieldChange('check_id', e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 h-10 px-4 text-sm text-slate-900 focus:border-slate-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Title</label>
                <input
                  value={newRule.title}
                  onChange={(e) => handleNewRuleFieldChange('title', e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 h-10 px-4 text-sm text-slate-900 focus:border-slate-400 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <textarea
                value={newRule.description}
                onChange={(e) => handleNewRuleFieldChange('description', e.target.value)}
                rows={4}
                className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 h-10 px-4 text-sm text-slate-900 focus:border-slate-400 focus:outline-none"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Cloud Provider</label>
                <input
                  value={newRule.cloud_provider}
                  onChange={(e) => handleNewRuleFieldChange('cloud_provider', e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 h-10 px-4 text-sm text-slate-900 focus:border-slate-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Service</label>
                <input
                  value={newRule.service}
                  onChange={(e) => handleNewRuleFieldChange('service', e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 h-10 px-4 text-sm text-slate-900 focus:border-slate-400 focus:outline-none"
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Category</label>
                <input
                  value={newRule.category}
                  onChange={(e) => handleNewRuleFieldChange('category', e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 h-10 px-4 text-sm text-slate-900 focus:border-slate-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Severity</label>
                <select
                  value={newRule.severity}
                  onChange={(e) => handleNewRuleFieldChange('severity', e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-white h-10 px-4 text-sm text-slate-900"
                >
                  <option value="">Select severity</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                  <option value="informational">Informational</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Status</label>
                <select
                  value={newRule.is_active ? 'true' : 'false'}
                  onChange={(e) => handleNewRuleFieldChange('is_active', e.target.value === 'true')}
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-white h-10 px-4 text-sm text-slate-900"
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>
            </div>

            {/* CIS Controls Mapping */}
            <div>
              <label className="text-sm font-medium text-slate-700">
                CIS Controls
              </label>

              <div className="mt-2">
                <CisControlsMultiSelectDropdown
                  controlList={cisControlOptions}
                  selected={newRule.cis_controls || []}
                  onChange={(controls) => handleNewRuleFieldChange("cis_controls", controls)}
                  disabled={createRuleLoading}
                />
              </div>
            </div>

            {/* Risk Mapping */}
            <div>
              <label className="text-sm font-medium text-slate-700">
                Risk Mapping
              </label>

              <div className="mt-2">
                <RiskMultiSelectDropdown
                  riskList={riskList}
                  selectedIds={newRule.risk_ids || []}
                  onChange={(ids) => handleNewRuleFieldChange("risk_ids", ids)}
                  disabled={createRuleLoading}
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 rounded-b-2xl border-t border-slate-200 px-6 py-4">
            <button
              type="button"
              onClick={closeAddRuleModal}
              className="rounded-xl h-10 px-5 text-sm font-medium border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreateRule}
              disabled={!isCreateRuleValid || createRuleLoading}
              className={`inline-flex items-center justify-center rounded-xl h-10 px-5 text-sm font-medium transition-colors ${
                !isCreateRuleValid || createRuleLoading
                  ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                  : "bg-[#2F2A7E] text-white hover:bg-[#27226B]"
              }`}
            >
              {createRuleLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFindings = () => {
    const displayedFindings = findings.filter((f) => {
      if (findingLocalFilters.control_name && !String(f.control_name || '').toLowerCase().includes(findingLocalFilters.control_name.toLowerCase())) return false;
      if (findingLocalFilters.severity && f.severity !== findingLocalFilters.severity) return false;
      return true;
    });

    const uniqueFindingSeverities = [...new Set(findings.map((f) => f.severity).filter(Boolean))];

    const findingColumns = [
      {
        id: 'control_id',
        accessorFn: (f) => f.control_id,
        header: 'Control ID',
        enableSorting: false,
        meta: { filter: { variant: 'text', label: 'Control ID', placeholder: 'Search control ID, name, risk mapping' } },
        cell: ({ row }) => <span className="text-slate-900">{row.original.control_id}</span>,
      },
      {
        id: 'control_name',
        accessorFn: (f) => f.control_name,
        header: 'Control Name',
        enableSorting: false,
        meta: { filter: { variant: 'text', label: 'Control Name', placeholder: 'Search control name' } },
        cell: ({ row }) => <span className="text-slate-700">{row.original.control_name}</span>,
      },
      {
        id: 'severity',
        accessorFn: (f) => f.severity,
        header: 'Severity',
        enableSorting: false,
        meta: {
          filter: {
            variant: 'select',
            label: 'Severity',
            placeholder: 'All severities',
            options: uniqueFindingSeverities.map((v) => ({ value: v, label: v })),
          },
        },
        cell: ({ row }) => (
          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${severityStyles[row.original.severity] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
            {row.original.severity}
          </span>
        ),
      },
      {
        id: 'status',
        accessorFn: (f) => f.status,
        header: 'Status',
        enableSorting: false,
        meta: {
          filter: {
            variant: 'select',
            label: 'Status',
            placeholder: 'All statuses',
            options: [
              { value: 'open', label: 'Open' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'resolved', label: 'Resolved' },
              { value: 'closed', label: 'Closed' },
              { value: 'suppressed', label: 'Suppressed' },
            ],
          },
        },
        cell: ({ row }) => (
          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusStyles[row.original.status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
            {row.original.status}
          </span>
        ),
      },
      {
        id: 'cis_controls',
        header: 'CIS / Framework Controls',
        enableSorting: false,
        cell: ({ row }) => {
          const finding = row.original;
          return Array.isArray(finding.cis_controls) && finding.cis_controls.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {finding.cis_controls.slice(0, 2).map((c, i) => (
                <span
                  key={i}
                  className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700"
                  title={c.control_name || ''}
                >
                  {c.framework}: {c.control_id}
                </span>
              ))}
              {finding.cis_controls.length > 2 && (
                <span className="text-[11px] text-slate-400">+{finding.cis_controls.length - 2} more</span>
              )}
            </div>
          ) : (
            <span className="text-xs text-slate-400">Not mapped yet</span>
          );
        },
      },
      {
        id: 'risk_mapping',
        header: 'Risk Mapping',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => openFindingRiskModal(row.original)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
            aria-label="View mapped risks and controls"
            title="View mapped risks and controls"
          >
            <Eye className="h-4 w-4" />
          </button>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          const finding = row.original;
          // update_finding_status/assign_finding/suppress_finding
          // (ai_cspm/views.py) are Platform-Admin-only endpoints - only
          // show the action when this specific finding's can_edit flag
          // AND the current user's role match, so we never render a
          // button that would 403.
          return finding.can_edit && isPlatformAdmin ? (
            <button
              type="button"
              disabled={findingActionLoading}
              onClick={() => handleResolveFinding(finding)}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              title="Mark as resolved"
            >
              <CheckIcon className="h-3.5 w-3.5" /> Resolve
            </button>
          ) : (
            <span className="text-xs text-slate-400">{finding.read_only ? 'Read only' : '-'}</span>
          );
        },
      },
    ];

    const findingColumnFilters = [
      findingSearch ? { id: 'control_id', value: findingSearch } : null,
      findingStatusFilter ? { id: 'status', value: findingStatusFilter } : null,
      findingLocalFilters.control_name ? { id: 'control_name', value: findingLocalFilters.control_name } : null,
      findingLocalFilters.severity ? { id: 'severity', value: findingLocalFilters.severity } : null,
    ].filter(Boolean);

    const handleFindingColumnFiltersChange = (updater) => {
      const next = typeof updater === 'function' ? updater(findingColumnFilters) : updater;
      const byId = Object.fromEntries(next.map((f) => [f.id, f.value]));
      setFindingSearch(byId.control_id || '');
      setFindingStatusFilter(byId.status || '');
      setFindingLocalFilters({
        control_name: byId.control_name || '',
        severity: byId.severity || '',
      });
      setFindingsPage(1);
    };

    return (
      <div className="space-y-4">
        <DataTable
          columns={findingColumns}
          data={displayedFindings}
          getRowId={(f, i) => String(f.control_id || i)}
          loading={tableLoading}
          manualPagination
          rowCount={findingsTotal}
          pagination={{ pageIndex: (findingsPage || 1) - 1, pageSize: DEFAULT_PAGE_SIZE }}
          onPaginationChange={(updater) => {
            const current = { pageIndex: (findingsPage || 1) - 1, pageSize: DEFAULT_PAGE_SIZE };
            const next = typeof updater === 'function' ? updater(current) : updater;
            setFindingsPage(next.pageIndex + 1);
          }}
          enableColumnFilters
          manualFiltering
          columnFilters={findingColumnFilters}
          onColumnFiltersChange={handleFindingColumnFiltersChange}
          emptyMessage="No findings match the current criteria."
          enableColumnVisibility={false}
          enableColumnResizing
          maxBodyHeight="65vh"
        />
      </div>
    );
  };

  const renderRemediation = () => {
    const displayedRemediation = remediation.filter((item) => {
      if (remediationLocalFilters.performed_by && !String(item.performed_by || '').toLowerCase().includes(remediationLocalFilters.performed_by.toLowerCase())) return false;
      if (remediationLocalFilters.notes && !String(item.notes || '').toLowerCase().includes(remediationLocalFilters.notes.toLowerCase())) return false;
      return true;
    });

    const remediationColumns = [
      {
        id: 'remediation_id',
        accessorFn: (item) => item.display_id || item.remediation_id || item.finding_id,
        header: 'Remediation ID',
        enableSorting: false,
        meta: { filter: { variant: 'text', label: 'Remediation ID', placeholder: 'Search control, recommendation, priority' } },
        cell: ({ row }) => (
          <span className="text-slate-900">
            {row.original.display_id || row.original.remediation_id || row.original.finding_id || 'N/A'}
          </span>
        ),
      },
      {
        id: 'action_type',
        accessorFn: (item) => item.action_type,
        header: 'Action Type',
        enableSorting: false,
        meta: {
          filter: {
            variant: 'select',
            label: 'Action Type',
            placeholder: 'All action types',
            options: [
              { value: 'manual_fix', label: 'Manual Fix' },
              { value: 'auto_remediated', label: 'Auto Remediated' },
              { value: 'suppressed', label: 'Suppressed' },
              { value: 'assigned', label: 'Assigned' },
              { value: 'ai_generated', label: 'AI Generated' },
            ],
          },
        },
        cell: ({ row }) => (
          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${actionTypeStyles[row.original.action_type] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
            {row.original.action_type}
          </span>
        ),
      },
      {
        id: 'performed_by',
        accessorFn: (item) => item.performed_by,
        header: 'Performed By',
        enableSorting: false,
        meta: { filter: { variant: 'text', label: 'Performed By', placeholder: 'Search performed by' } },
        cell: ({ row }) => <span className="text-slate-700">{row.original.performed_by}</span>,
      },
      {
        id: 'notes',
        accessorFn: (item) => item.notes,
        header: 'Notes',
        enableSorting: false,
        meta: { filter: { variant: 'text', label: 'Notes', placeholder: 'Search notes' } },
        cell: ({ row }) => <span>{row.original.notes}</span>,
      },
      {
        id: 'created_at',
        accessorFn: (item) => item.created_at,
        header: 'Created At',
        enableSorting: false,
        cell: ({ row }) => <span>{formatDate(row.original.created_at)}</span>,
      },
    ];

    const remediationColumnFilters = [
      remediationSearch ? { id: 'remediation_id', value: remediationSearch } : null,
      remediationActionTypeFilter ? { id: 'action_type', value: remediationActionTypeFilter } : null,
      remediationLocalFilters.performed_by ? { id: 'performed_by', value: remediationLocalFilters.performed_by } : null,
      remediationLocalFilters.notes ? { id: 'notes', value: remediationLocalFilters.notes } : null,
    ].filter(Boolean);

    const handleRemediationColumnFiltersChange = (updater) => {
      const next = typeof updater === 'function' ? updater(remediationColumnFilters) : updater;
      const byId = Object.fromEntries(next.map((f) => [f.id, f.value]));
      setRemediationSearch(byId.remediation_id || '');
      setRemediationActionTypeFilter(byId.action_type || '');
      setRemediationLocalFilters({
        performed_by: byId.performed_by || '',
        notes: byId.notes || '',
      });
      setRemediationPage(1);
    };

    return (
      <div className="space-y-4">
        <DataTable
          columns={remediationColumns}
          data={displayedRemediation}
          getRowId={(item, i) => String(item.remediation_id || item.finding_id || i)}
          loading={tableLoading}
          manualPagination
          rowCount={remediationTotal}
          pagination={{ pageIndex: (remediationPage || 1) - 1, pageSize: DEFAULT_PAGE_SIZE }}
          onPaginationChange={(updater) => {
            const current = { pageIndex: (remediationPage || 1) - 1, pageSize: DEFAULT_PAGE_SIZE };
            const next = typeof updater === 'function' ? updater(current) : updater;
            setRemediationPage(next.pageIndex + 1);
          }}
          enableColumnFilters
          manualFiltering
          columnFilters={remediationColumnFilters}
          onColumnFiltersChange={handleRemediationColumnFiltersChange}
          emptyMessage="No remediation recommendations found."
          enableColumnVisibility={false}
          enableColumnResizing
          maxBodyHeight="65vh"
        />
      </div>
    );
  };

  const renderActiveTab = () => {
    if (loading) {
  return (
    <div className="flex items-center justify-center h-screen bg-white">
      <ApiLoader
        variant="spinner"
        size={50}
        label="Loading CSPM..."
      />
    </div>
  );
}

  if (tableLoading) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white py-24">
      <ApiLoader
        variant="spinner"
        size={50}
        label={loadingMessage}
      />
    </div>
  );
}
    switch (activeTab) {
      case 'assets':
        return renderAssetTable();
      case 'rules':
        return renderRules();
      case 'findings':
        return renderFindings();
      case 'remediation':
        return renderRemediation();
      default:
        return (
        <DashboardOverview
            dashboardStats={dashboardStats}
            assetsByProvider={assetsByProvider}
            assets={assets}
            rules={rules}
            findings={findings}
            remediationPlans={remediation}
            loadDashboard={loadDashboard}
            onViewAllFindings={() => setActiveTab('findings')}
            scans={scans}
            scansLoading={scansLoading}
            connectorOptions={connectorOptions}
            selectedConnectorId={selectedConnectorId}
            onSelectedConnectorChange={setSelectedConnectorId}
            onTriggerScan={handleTriggerScan}
            onRescan={handleRescan}
            scanTriggerLoading={scanTriggerLoading}
            rescanLoadingId={rescanLoadingId}
            canTriggerScans={canTriggerScans}
        />
    );
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-full mx-auto dark:bg-[#0f172a] min-h-screen">
      <CRMModuleHeader
        icon={ShieldCheckIcon}
        title="Cloud Security Posture Management"
        description="Monitor cloud assets, findings, rules and remediation."
        rightContent={(
          <>
            {/* <button
              onClick={loadDashboard}
              className="inline-flex items-center justify-center rounded-lg h-10 px-4 text-sm font-medium text-white hover:opacity-90"
              style={{ backgroundColor: 'var(--app-primary-color, #26256b)' }}
            >
              Refresh Metrics
            </button>
            <button
              onClick={() => setActiveTab('assets')}
              className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white h-10 px-4 text-sm font-medium text-slate-900 hover:bg-slate-50"
            >
              View Asset Inventory
            </button> */}
          </>
        )}
      />

      <TabNavigation
        tabs={tabs.map((tab) => ({ id: tab.id, name: tab.label, icon: tab.icon }))}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {renderActiveTab()}
      {renderViewRuleModal()}
      {renderFindingRiskModal()}
      {renderRuleOwnershipModal()}
      {renderEditRuleModal()}
      {renderDeleteRuleModal()}
      {renderAddRuleModal()}
    </div>
  );
};

export default Ai_CSPM;